import type { PublicCard, RoomState } from '@tv-casino/shared';

/** The cues a table can play, by name — the shared vocabulary between the
 * browser's sample player and any native engine built against this package. */
export type SoundCue =
  | 'chip'
  | 'ballDrop'
  | 'dice'
  | 'payout'
  | 'sweep'
  | 'card'
  | 'cardFlip'
  | 'shuffle'
  | 'bigWin'
  | 'jackpot';

/** How loud the floor behind the table is: between hands, betting, or dealing. */
export type AmbienceLevel = 'idle' | 'betting' | 'play';

/**
 * How loud the casino floor sits behind the table. Between hands — the lobby,
 * the payout beat, the break, the leaderboard — the room is the soundtrack;
 * while bets go down it steps back; once cards or dice are live it sits low
 * enough that every cue still reads.
 */
export function ambienceLevel(state: RoomState | null): AmbienceLevel {
  if (!state || state.leaderboardShown || state.qrShown || state.deadline?.kind === 'break') return 'idle';
  switch (state.phase) {
    case 'lobby':
    case 'payout':
      return 'idle';
    case 'betting':
      return 'betting';
    default:
      return 'play';
  }
}

/**
 * What the TV listens to between two broadcasts. Every card table reduces to
 * the same few numbers — how many cards are out, how many are face up, how
 * much is staked, whether the hand has settled — so one comparison drives the
 * soundtrack for blackjack, Hold'em, baccarat and three-card poker alike.
 *
 * Roulette and craps keep their own hooks for the wheel and the dice; craps
 * only borrows the chip click from here. Keno has no wheel or dice to hook,
 * so it rides this comparison whole: a chip per ticket, pay or sweep on settle.
 */
export interface CueSnapshot {
  gameType: RoomState['gameType'];
  cardsHidden: boolean;
  /** Card slots on the felt, face down included. */
  cards: number;
  faceUp: number;
  /** Chips on the layout this hand. */
  staked: number;
  settled: boolean;
  /** At least one stake came back with something. */
  paid: boolean;
  shoe: number;
}

const up = (cards: PublicCard[]): number => cards.filter((c) => c.faceUp).length;
const sum = (values: number[]): number => values.reduce((n, v) => n + v, 0);

export function cueSnapshot(state: RoomState | null): CueSnapshot | null {
  if (!state) return null;
  const base = {
    gameType: state.gameType,
    cardsHidden: state.cardsHidden,
    shoe: state.shoeRemaining,
    cards: 0,
    faceUp: 0,
    staked: 0,
    settled: false,
    paid: false,
  };

  switch (state.gameType) {
    case 'blackjack': {
      const hands = state.seats.flatMap((s) => s.hands);
      const cards = [...state.dealer.cards, ...hands.flatMap((h) => h.cards)];
      const settled = state.phase === 'payout' && hands.some((h) => h.result !== null);
      return {
        ...base,
        cards: cards.length,
        faceUp: up(cards),
        staked: sum(
          state.seats.map(
            (s) =>
              (s.hands.length ? sum(s.hands.map((h) => h.bet)) : s.bet) +
              sum(s.sideBets.map((b) => b.stake)) +
              s.insuranceBet,
          ),
        ),
        settled,
        paid:
          settled &&
          state.seats.some(
            (s) =>
              s.hands.some((h) => (h.payout ?? 0) > 0) ||
              s.sideBets.some((b) => (b.payout ?? 0) > 0) ||
              (s.insurancePayout ?? 0) > 0,
          ),
      };
    }
    case 'holdem': {
      const h = state.holdem;
      if (!h) return base;
      const cards = [...h.board, ...h.seats.flatMap((s) => s.cards)];
      return {
        ...base,
        cards: cards.length,
        faceUp: up(cards),
        staked: sum(h.seats.map((s) => s.totalCommitted)),
        settled: h.results !== null,
        // A pot always goes to somebody.
        paid: h.results !== null,
      };
    }
    case 'baccarat': {
      const b = state.baccarat;
      if (!b) return base;
      const cards = [...b.playerCards, ...b.bankerCards];
      return {
        ...base,
        cards: cards.length,
        faceUp: up(cards),
        staked:
          sum(state.seats.map((s) => s.bet)) +
          sum(b.seats.flatMap((s) => s.sideBets.map((x) => x.stake))),
        settled: b.results !== null,
        paid: (b.results ?? []).some((r) => r.payout > 0),
      };
    }
    case 'threecard': {
      const t = state.threecard;
      if (!t) return base;
      const cards = [...t.dealerCards, ...t.seats.flatMap((s) => s.cards)];
      return {
        ...base,
        cards: cards.length,
        faceUp: up(cards),
        staked:
          sum(state.seats.map((s) => s.bet)) +
          sum(t.seats.map((s) => s.play + (s.pairPlus?.stake ?? 0))),
        settled: t.results !== null,
        paid: (t.results ?? []).some((r) => r.payout > 0),
      };
    }
    case 'craps': {
      const c = state.craps;
      if (!c) return base;
      return { ...base, staked: sum(c.seats.flatMap((s) => s.bets.map((b) => b.amount))) };
    }
    case 'roulette':
      return base;
    case 'keno': {
      const k = state.keno;
      if (!k) return base;
      return {
        ...base,
        staked: sum(k.seats.flatMap((s) => s.tickets.map((t) => t.amount))),
        settled: k.results !== null,
        paid: (k.results ?? []).some((r) => r.payout > 0),
      };
    }
  }
}

/**
 * The cues one broadcast earns, in the order they should sound. The first
 * snapshot, and the first after a game swap, only primes: a TV that pairs
 * mid-hand should not replay the whole deal at once.
 */
export function tableCues(prev: CueSnapshot | null, next: CueSnapshot | null): SoundCue[] {
  if (!prev || !next || prev.gameType !== next.gameType) return [];
  const cues: SoundCue[] = [];
  if (next.shoe > prev.shoe) cues.push('shuffle');
  if (next.staked > prev.staked) cues.push('chip');
  // The host turning every card over is a display change, not a deal.
  if (prev.cardsHidden === next.cardsHidden) {
    if (next.cards > prev.cards) cues.push('card');
    else if (next.faceUp > prev.faceUp) cues.push('cardFlip');
  }
  if (next.settled && !prev.settled) cues.push(next.paid ? 'payout' : 'sweep');
  return cues;
}
